<template>
  <div class="mobile-product-page">
    <breadcrumbs :categories="categories" />

    <mobile-slider
      :key="productId || ''"
      :photos="product.photos"
      :alt="product.title"
      :circularPhotos="product.circularPhotos.map((e: any) => e.link.high)"
      :video="product.video"
      :isShownCircular="isShownCircular"
      :isShownVideo="isShownVideo"
      :active-photo-key="activePhotoKey"
      @changeStyleThumbIndicator="styleThumbIndicator = $event"
      @closeCircular="closeCircular"
      @closeVideo="closeVideo"
      @active-photo-change="(key: any) => (activePhotoKey = key)"
    />

    <indicators
      :hasCircular="hasCircular"
      :hasVideo="hasVideo"
      :isShownCircular="isShownCircular"
      :isShownVideo="isShownVideo"
      :styleThumbIndicator="styleThumbIndicator"
      :isShowProgress="product.photos.length > 1"
      @showCircular="showCircular"
      @showVideo="showVideo"
    />

    <div class="description-container">
      <span class="rectangle" />
      <div v-if="skuSelectorData.products.length > 0" class="sku-selector">
        <SkuSelector
          v-model="selectedSkuProduct"
          :products="skuSelectorData.products"
          :characteristics="skuSelectorData.characteristics"
        />
      </div>
      <div v-if="isBuyingDayBannerShown" class="shopping-day-banner">
        <shopping-day-banner
          link="https://gift.promo.kazanexpress.ru/"
          page-name="PRODUCT"
          text="Оформите заказ от 1000 ₽ — участвуйте в мегааарозыгрыше"
        />
      </div>
      <product-header mobile class="product-header" />
      <price-set
        class="price-set"
        :sellPrice="currentSku.purchasePrice"
        :fullPrice="currentSku.fullPrice"
        :badges="badges"
        :isBestPrice="false"
        :banners="banners"
        :discount-timer="currentSku.discountTimer"
        :available="currentSku.availableAmount"
        :installments="[]"
      />
      <characteristics
        :key="product.id"
        showModalButton
        :characteristics="characteristics"
        :is-cropped="true"
      />
      <button v-if="characteristics" class="button" @click="openFullCharacteristics">
        <span class="text">Все характеристики</span>
      </button>
      <short-description
        v-if="product.shortDescription"
        class="short-description"
        :shortDescription="{
          // upperText: 'Описание', // Убрано для устранения дублирования
          text: product.shortDescription,
          list: product.attributes || [],
        }"
      />
      <button class="button" @click="openFullDescription">
        <span class="text">Полное описание</span>
      </button>
      <OtherTabs v-if="hasTabs" class="other-tabs" />
      <seller
        v-if="seller"
        class="seller"
        :seller="{
          title: seller.title,
          // name: seller.title,
          avatar: seller.avatar,
          link: seller.link,
          rating: seller.rating,
          reviews: seller.feedbackQuantity,
          feedbackQuantity: seller.feedbackQuantity,
          seller: seller.seller,
        }"
      />
      <feedback
        :key="`${product.id}-feedback`"
        :productId="product.id!"
        :reviewsAmount="product.feedbackQuantity"
      />

      <combo-products v-if="isComboShown" :bundle="comboBundle" />
      <bottom-slides :key="productId" />
    </div>

    <!-- Fixed CTA block in bottom bar -->
    <Teleport to="#bottom-bar-additional">
      <PriceBlock
        :sellPrice="currentSku.purchasePrice || 0"
        :fullPrice="currentSku.fullPrice || 0"
        :availableAmount="currentSku.availableAmount || 0"
      />
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import type { IProduct } from '../../models/product';
// TODO: Replace with actual components when available
// import ComboProducts from '@/features/combo-products/ui/combo-products/index.vue';
import MobileSlider from './slider.vue';
import ProductHeader from '../common/product-header.vue';
// import Tabs from '@/pages/product/ui/common/tabs-old/tabs.vue';
import PriceSet from './price-set.vue';
import Characteristics from './characteristics.vue';
import Indicators from './indicators.vue';
import ShortDescription from '../common/short-description.vue';
// import DescriptionSet from './description-set.vue';
import Description from './description.vue';
import Seller from '../common/seller.vue';
// import Categories from '../common/categories.vue';
// TODO: Replace with proper icon system
// import IconUi from '@/shared/legacy-kit/components/icon/index.vue';
import { SkuSelector } from '@/features/sku-selector';
import { useProductPage } from '../../business/use-product-page';
import { useProductModals } from '../../business/useProductModals';

// TODO: Replace with actual components when available
// import FullScreenGallery from '@/shared/components/ui/full-screen-gallery.vue';
// import ProductsSlide from '@/shared/components/ui/products-slide.vue';
import { FeatureFlags } from '@/features/feature-flags';
import type { ISkuItem } from '../../mappers/product-to-sku-list';
// TODO: Replace with actual types when available
// import { Seller as ISeller } from '@/shared/types/models/seller';
import type { IBanner } from '../../models/banner';
import type { TSkuCategory } from '../../mappers/product-to-categories';
// import BottomWrapper from '@/pages/product/ui/common/tabs-old/bottom-wrapper.vue';
import type { IBadge } from '../../models/badge';
import type { ISkuPhotoItemCharacteristic } from '../../models/slider';
import type { ICharacteristics } from '../../models/characteristic';
import BottomSlides from '../common/bottom-slides.vue';
// TODO: Replace with actual analytics when available
// import { ProductListType } from '@mm-frontend/frontend-bunseki';
// import { ProductImpression } from '@/shared/types/models/productImpression';
import Feedback from './feedback.vue';
import Breadcrumbs from './breadcrumbs.vue';
// TODO: Replace with actual popup service when available
// import { PopupsContainerModel, PopupsService } from '@mm-frontend/common-popups-service';
// import CharacteristicsModal from '@/pages/product/ui/mobile/characteristic-modal.vue';
// TODO: Replace with actual banner when available
// import ShoppingDayBanner from '@/shared/product/ui/shopping-day-banner/shopping-day-banner.vue';
import OtherTabs from './OtherTabs.vue';
import PriceBlock from './price-block.vue';
import { useLastSeenProductsStore } from '~/features/store';
// TODO: Replace with actual composables when available
// import { useBottomBar } from '@/features/bottom-bar/business/useBottomBar';
// import { useProduct } from '@/pages/product/business/composableProduct';

// Temporary types
type ISeller = any;
type PopupsContainerModel = any;
type PopupsService = any;

// Temporary components
const ComboProducts = { template: '<div><!-- TODO: ComboProducts --></div>' };
// ButtonSecondaryNeutral imported above
const ShoppingDayBanner = { template: '<div><!-- TODO: ShoppingDayBanner --></div>' };

const ProductListType = {};
const ProductImpression = { PLID: {} };

interface Props {
  product: IProduct;
}

const props = withDefaults(defineProps<Props>(), {
  product: () => ({}) as IProduct,
});

// Reactive state
const activePhotoKey = ref<string | null>(null);
const isBuyingDayBannerShown = ref(false);
const isComboAvailable = ref(false);
const isShownCircular = ref(false);
const isShownVideo = ref(false);
const styleThumbIndicator = ref({
  transform: 'translateX(0px)',
});

// Selected SKU product for SkuSelector
const selectedSkuProduct = ref();

// Computed properties
const productId = computed(() => props.product?.id || null);

const { state, getProductBadges, categories, skuSelectorData, selectSku, productEntity } =
  useProductPage();

const { addProduct } = useLastSeenProductsStore();
addProduct(productEntity.value);

const {
  openCharacteristicsModal,
  closeCharacteristicsModal,
  openDescriptionModal,
  closeDescriptionModal,
  openFeedbackModal,
  closeFeedbackModal,
} = useProductModals();
const currentSku = computed((): ISkuItem => {
  return (state.currentSku || {}) as ISkuItem;
});

const banners = computed((): IBanner[] => {
  return (state.banners || []) as IBanner[];
});

const seller = computed((): ISeller => {
  return props.product.seller;
});

const availableAmount = computed(() => {
  return state.currentSku?.availableAmount;
});

const showSimilar = computed((): boolean => {
  const amount =
    props.product.skuList?.reduce((sum: number, sku: any) => sum + sku.availableAmount, 0) || 0;
  return amount === 0;
});

const characteristics = computed((): ICharacteristics => {
  return (state.characteristics || {}) as ICharacteristics;
});

const comboBundle = computed(() => {
  return state.bundle;
});

const isComboShown = computed(() => {
  return (
    isComboAvailable.value &&
    availableAmount.value &&
    availableAmount.value > 0 &&
    comboBundle.value
  );
});

const hasCircular = computed((): boolean => {
  return props.product.circularPhotos && props.product.circularPhotos?.length > 0;
});

const hasVideo = computed((): boolean => {
  return Boolean(props.product?.video) && (props.product?.video || '')?.length > 0;
});

// const productData = computed((): IProduct => {
//   return dataService.getState().product;
// });

const hasTabs = computed((): boolean => {
  return !!state.tabs?.length;
});

const badges = computed((): IBadge[] => {
  const discountBadge = currentSku.value?.discountBadge;

  return [
    ...(discountBadge
      ? ([
          {
            id: -9,
            label: discountBadge.text,
            backgroundColor: discountBadge.backgroundColor,
            color: discountBadge.textColor,
          },
        ] as IBadge[])
      : []),
    ...getProductBadges(props.product),
  ];
});

// const reviewsPhotoAmount = computed((): number => {
//   return dataService.getState().reviewsPhotoAmount;
// });

// Constants
const eProductListType = ProductListType;
const eProductImpressionPLID = ProductImpression.PLID;
// const eSKUImageStyle = ESKUImageStyle; // No longer needed with new SkuSelector

// Methods
const setIsComboAvailable = (): void => {
  console.log('TODO: FeatureFlags.COMBO()'); // TODO: Replace with actual feature flag
  // FeatureFlags.COMBO().then((isComboAvailableValue: boolean) => (isComboAvailable.value = isComboAvailableValue));
};

const scrollTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const showCircular = () => {
  isShownCircular.value = true;
  scrollTop();
};

const showVideo = () => {
  isShownVideo.value = true;
  scrollTop();
};

const closeCircular = () => {
  isShownCircular.value = false;
};

const closeVideo = () => {
  isShownVideo.value = false;
};

const openFullDescription = () => {
  if (props.product.description) {
    openDescriptionModal(props.product.description);
  }
};

const openFullCharacteristics = () => {
  if (characteristics.value) {
    openCharacteristicsModal(characteristics.value);
  }
};

// Watchers
watch(
  currentSku,
  () => {
    const photoCharacteristic: ISkuPhotoItemCharacteristic =
      currentSku.value?.characteristics?.find(
        (characteristic: any) => characteristic.type === 'image'
      ) as ISkuPhotoItemCharacteristic;

    if (!photoCharacteristic) return;
    activePhotoKey.value = photoCharacteristic.value.photoKey;
    console.log(photoCharacteristic);
  },
  { immediate: true }
);

// Watch for selectedSkuProduct changes from SkuSelector and update current SKU
watch(selectedSkuProduct, async (newProduct) => {
  if (newProduct?.skuId && state.product) {
    const router = useRouter();
    const skuId = parseInt(newProduct.skuId);
    // Проверяем, что SKU действительно изменился
    if (state.currentSku?.id !== skuId) {
      // Use selectSku to update current SKU, which will also update URL, price, and gallery
      await selectSku(skuId, router);
    }
  }
});

// Sync selectedSkuProduct with currentSku when currentSku changes
watch(
  () => state.currentSku,
  (newSku) => {
    if (newSku && skuSelectorData.value.products.length > 0) {
      const matchingProduct = skuSelectorData.value.products.find(
        (p) => p.skuId === newSku.id?.toString()
      );
      if (matchingProduct && selectedSkuProduct.value?.skuId !== matchingProduct.skuId) {
        selectedSkuProduct.value = matchingProduct;
      }
    }
  },
  { immediate: true }
);

// Lifecycle
onMounted(async () => {
  setIsComboAvailable();

  // Feature flags
  try {
    console.log('TODO: FeatureFlags.PRODUCT_BANNER()'); // TODO: Replace with actual feature flag
    // const isShown = await FeatureFlags.PRODUCT_BANNER();
    // isBuyingDayBannerShown.value = isShown;
  } catch (error) {
    console.error('Feature flag error:', error);
  }
});
</script>

<style lang="stylus" scoped>

.t
  position: relative;
  background-color: #ffffff;

.mobile-product-page
  position relative

  .description
    display none
    position absolute

  .description-container
    position relative
    display flex
    flex-direction column
    padding 0 16px
    background-color #ffffff
    border-radius 24px 24px 0 0
    z-index 3

    +Media(Tablet)
      padding 0 24px

    .rectangle
      margin 8px 0 0 calc(50% - 16px)
      width 32px
      height 4px
      background-color hsla(234, 10%, 92%, 1)
      border-radius 100px

    .product-header
      padding-top 24px

    .sku-selector
      margin-top 32px
      width 100%

    .price-set
      margin-top 40px

    .characteristics, .short-description
      margin-top 48px

    .description-set
      margin-top 36px

    .seller
      margin-top 52px

    .categories
      margin-top 48px

  >>> .combo-product
    margin-top 32px

.button
  margin-top 32px
  width 100%
  height 48px
  background-color #f0f1f5
  border none
  border-radius 12px
  cursor pointer
  font-family Inter
  font-size 14px
  font-weight 500
  color #15151a
  transition all 0.2s ease

  &:hover
    background-color #EAECF0

.other-tabs
  margin-top 32px
  width 100%
.shopping-day-banner
  margin-top 32px
</style>
